import React, {
  useMemo,
  useState,
} from "react";
import {
  UserPlus,
  X,
} from "lucide-react";

import { RecipientForm } from "../../components/recipient/RecipientForm";

import { useCorridor } from "../../hooks/useCorridor";
import {
  usePayoutBanks,
  usePayoutCoverage,
} from "../../hooks/useMe";
import { useCountries } from "../../hooks/useCountries";
import { useSupportedRecipientCountries } from "../../hooks/useSupportedRecipientCountries";
import { useSupportedChannels } from "../../hooks/useSupportedChannels";
import { safeApiErrorMessage } from "../../utils/safeApiErrorMessage";

export function RecipientFormCard({
  createRecipient,
  onCancel,
  isCreatingRecipient = false,
}) {
  const corridor = useCorridor();

  const [nickname, setNickname] =
    useState("");

  const [
    firstName,
    setFirstName,
  ] = useState("");

  const [
    lastName,
    setLastName,
  ] = useState("");

  const [
    submitError,
    setSubmitError,
  ] = useState(null);

  const [
    submittingLocally,
    setSubmittingLocally,
  ] = useState(false);

  const { countries } =
    useCountries();

  const { isChannelSupported } =
    usePayoutCoverage();

  const { countryOptions } =
    useSupportedRecipientCountries(
      corridor.countries,
      countries
    );

  const selectedCountry =
    useMemo(
      () =>
        countryOptions.find(
          (country) =>
            country.value ===
            corridor.countryCode
        ) || null,
      [
        countryOptions,
        corridor.countryCode,
      ]
    );

  const selectedCountryData =
    useMemo(
      () =>
        countries.find(
          (country) =>
            country.code?.toUpperCase() ===
            corridor.countryCode?.toUpperCase()
        ) || null,
      [
        countries,
        corridor.countryCode,
      ]
    );

  const recipientDialCode =
    selectedCountryData?.dialCode ||
    corridor.getDialCode() ||
    "";

  const isBankTransferWithBanks =
    isChannelSupported(
      corridor.countryCode,
      "bank_transfer"
    ) &&
    corridor.channelKey ===
      "bank_transfer";

  const {
    banks,
    isLoadingBanks,
  } = usePayoutBanks(
    isBankTransferWithBanks
      ? corridor.countryCode
      : null
  );

  const channelEntries =
    useSupportedChannels(
      corridor.countryCode,
      corridor.availableChannels
    );

  const fullName =
    `${firstName} ${lastName}`.trim();

  const submitting =
    submittingLocally ||
    isCreatingRecipient;

  async function handleCreate() {
    setSubmitError(null);

    if (!corridor.countryCode) {
      setSubmitError(
        "Please select a country."
      );
      return;
    }

    if (!corridor.channelKey) {
      setSubmitError(
        "Please select how the recipient will receive funds."
      );
      return;
    }

    if (!fullName) {
      setSubmitError(
        "Please enter the recipient's name."
      );
      return;
    }

    if (!corridor.validate()) {
      setSubmitError(
        "Please fix the highlighted fields."
      );
      return;
    }

    const paymentMethod =
      corridor.buildPaymentMethodPayload();

    if (!paymentMethod) {
      setSubmitError(
        "Please fix the highlighted fields."
      );
      return;
    }

    const payload = {
      nickname:
        nickname || fullName,

      full_name:
        fullName,

      country_code:
        corridor.countryCode,

      currency:
        paymentMethod.currency,

      default_channel:
        paymentMethod.channel,

      default_provider:
        paymentMethod.provider,

      status:
        "active",

      payment_method:
        paymentMethod,
    };

    setSubmittingLocally(true);

    try {
      /*
       * This calls the chat hook's createRecipient().
       *
       * That function performs createRecipientAsync()
       * and dispatches RECIPIENT_CREATED when the API
       * succeeds.
       */
      await createRecipient(
        payload
      );
    } catch (error) {
      setSubmitError(
        safeApiErrorMessage(
          error,
          "Something went wrong creating the recipient."
        )
      );
    } finally {
      setSubmittingLocally(false);
    }
  }

  return (
    <div className="w-full max-w-[520px] overflow-hidden rounded-3xl border border-[#0A4730]/10 bg-[#FDFDFD] shadow-[0_10px_40px_rgba(10,71,48,0.12)]">
      <div className="flex items-center justify-between border-b border-[#0A4730]/10 bg-white px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#C0F6B7]/50 text-[#0A4730]">
            <UserPlus
              size={18}
            />
          </div>

          <div>
            <p className="text-sm font-semibold text-[#0A4730]">
              New recipient
            </p>

            <p className="mt-0.5 text-[11px] text-[#0A2540]/45">
              Add their payout details
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          aria-label="Cancel adding recipient"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0A4730]/5 text-[#0A4730]/60 transition hover:bg-[#0A4730]/10 active:scale-95 disabled:opacity-40"
        >
          <X size={17} />
        </button>
      </div>

      <div className="max-h-[65vh] overflow-y-auto p-4">
        <RecipientForm
          compact
          nickname={nickname}
          setNickname={
            setNickname
          }
          firstName={
            firstName
          }
          setFirstName={
            setFirstName
          }
          lastName={
            lastName
          }
          setLastName={
            setLastName
          }
          corridor={
            corridor
          }
          countryOptions={
            countryOptions
          }
          selectedCountry={
            selectedCountry
          }
          channelEntries={
            channelEntries
          }
          recipientDialCode={
            recipientDialCode
          }
          banks={banks}
          isLoadingBanks={
            isLoadingBanks
          }
          submitting={
            submitting
          }
          submitError={
            submitError
          }

          /*
           * The normal page has Save + Send.
           *
           * Inside the send-money chat there is only
           * one useful action: create it and continue
           * the current transfer.
           */
          showSaveAction={false}
          sendLabel="Add recipient"
          onSend={
            handleCreate
          }
        />
      </div>
    </div>
  );
}